import { v4 as uuidv4 } from 'uuid';

import * as Sentry from '~/services/sentry';

import { REASON } from './reasons';
import { reportWarning, trace } from './report';
import { DOMAIN } from './types';

type AttemptOutcome = 'paid' | 'failed' | 'cancelled';

type StallKind =
  | 'payment_not_started'
  | 'left_before_payment'
  | 'payment_not_completed';

interface AttemptStep {
  step: string;
  atMs: number;
  data?: Record<string, unknown>;
}

interface Attempt {
  id: string;
  startedAt: number;
  steps: AttemptStep[];
  orderId: string | null;
  idempotencyKey: string | null;
  paymentStartedVia: string | null;
  isBusy: () => boolean;
  context: Record<string, unknown>;
  reportedStalls: Set<StallKind>;
  finished: boolean;
}

const PAYMENT_START_DEADLINE_MS = 30_000;
const PAYMENT_COMPLETION_DEADLINE_MS = 5 * 60_000;
const MAX_STEPS = 40;

let attempt: Attempt | null = null;
let deadline: ReturnType<typeof setTimeout> | null = null;

const clearDeadline = () => {
  if (deadline) {
    clearTimeout(deadline);
    deadline = null;
  }
};

const clearTags = () => {
  Sentry.setTag('checkout_attempt_id', undefined);
  Sentry.setTag('order_id', undefined);
  Sentry.setTag('idempotency_key', undefined);
};

const elapsed = (current: Attempt) => Date.now() - current.startedAt;

const lastStep = (current: Attempt) =>
  current.steps[current.steps.length - 1]?.step ?? 'none';

const reportStall = (current: Attempt, kind: StallKind) => {
  if (current.reportedStalls.has(kind) || current.finished) {
    return;
  }
  current.reportedStalls.add(kind);
  reportWarning(DOMAIN.checkout, 'Checkout attempt stalled', {
    reason: REASON.checkout.attemptStalled,
    fingerprint: [kind, lastStep(current)],
    extras: {
      kind,
      attemptId: current.id,
      orderId: current.orderId,
      idempotencyKey: current.idempotencyKey,
      lastStep: lastStep(current),
      paymentStartedVia: current.paymentStartedVia,
      elapsedMs: elapsed(current),
      ...current.context,
      steps: current.steps.map(
        ({ step, atMs, data }) =>
          `+${atMs}ms ${step}${data ? ` ${JSON.stringify(data)}` : ''}`,
      ),
    },
  });
};

const handleVisibility = () => {
  if (
    document.visibilityState !== 'hidden' ||
    !attempt ||
    attempt.paymentStartedVia ||
    !attempt.isBusy()
  ) {
    return;
  }
  reportStall(attempt, 'left_before_payment');
};

const stopWatchingVisibility = () => {
  if (typeof document !== 'undefined') {
    document.removeEventListener('visibilitychange', handleVisibility);
  }
};

const armDeadline = (current: Attempt, ms: number, kind: StallKind) => {
  clearDeadline();
  deadline = setTimeout(() => {
    deadline = null;
    if (attempt !== current || current.finished) {
      return;
    }
    if (!current.isBusy()) {
      current.finished = true;
      stopWatchingVisibility();
      clearTags();
      return;
    }
    reportStall(current, kind);
  }, ms);
};

const step = (name: string, data?: Record<string, unknown>) => {
  trace(DOMAIN.checkout, `Attempt: ${name}`, {
    attemptId: attempt?.id,
    ...data,
  });
  if (!attempt || attempt.finished) {
    return;
  }
  if (attempt.steps.length >= MAX_STEPS) {
    attempt.steps.splice(1, 1);
  }
  attempt.steps.push({ step: name, atMs: elapsed(attempt), data });
};

const start = (
  isBusy: () => boolean,
  context: Record<string, unknown> = {},
) => {
  if (attempt && !attempt.finished && attempt.isBusy()) {
    step('resumed', context);
    return attempt.id;
  }
  clearDeadline();
  stopWatchingVisibility();
  attempt = {
    id: uuidv4(),
    startedAt: Date.now(),
    steps: [],
    orderId: null,
    idempotencyKey: null,
    paymentStartedVia: null,
    isBusy,
    context,
    reportedStalls: new Set(),
    finished: false,
  };
  clearTags();
  Sentry.setTag('checkout_attempt_id', attempt.id);
  step('started', context);
  armDeadline(attempt, PAYMENT_START_DEADLINE_MS, 'payment_not_started');
  document.addEventListener('visibilitychange', handleVisibility);
  return attempt.id;
};

const setIdempotencyKey = (key: string) => {
  if (!attempt || attempt.finished) {
    return;
  }
  attempt.idempotencyKey = key;
  Sentry.setTag('idempotency_key', key);
};

const setOrderId = (orderId: string) => {
  if (!orderId || !attempt || attempt.finished) {
    return;
  }
  Sentry.setTag('order_id', orderId);
  attempt.orderId = orderId;
  step('order_created', { orderId });
};

const paymentStarted = (kind: string, data?: Record<string, unknown>) => {
  step(`payment_started:${kind}`, data);
  if (!attempt || attempt.finished) {
    return;
  }
  attempt.paymentStartedVia = kind;
  stopWatchingVisibility();
  armDeadline(attempt, PAYMENT_COMPLETION_DEADLINE_MS, 'payment_not_completed');
};

const finish = (outcome: AttemptOutcome, data?: Record<string, unknown>) => {
  if (!attempt || attempt.finished) {
    return;
  }
  step(`finished:${outcome}`, data);
  attempt.finished = true;
  clearDeadline();
  stopWatchingVisibility();
  clearTags();
};

export const checkoutAttempt = {
  start,
  step,
  setIdempotencyKey,
  setOrderId,
  paymentStarted,
  finish,
};
